<template>
  <div class="chatter-drawer-root">
    <ChecklistDrawer
      v-if="!combinedEnabled"
      :drawer="drawer"
      :icon-name="iconName"
      :type="type"
      :type-value="typeValue"
      :list-data="listData"
      :chatter-list="chatterList"
      :total-checkpoint-chatter="totalCheckpointChatter"
      :loading="loading"
      :is-employee-chatter="isEmployeeChatter"
      :avatar="avatar"
      :badge-status="badgeStatus"
      @closeDrawer="onLegacyClose"
      @toggle-badge-status="$emit('toggle-badge-status', $event)"
      @requestMoreCheckpointsChatter="$emit('requestMoreCheckpointsChatter', $event)"
    />

    <v-navigation-drawer
      v-else
      right
      v-model="localDrawer"
      app
      temporary
      class="chatter-drawer"
      :z-index="100"
      :style="{ width: drawerWidth + 'px' }"
    >
      <div class="chatter-drawer__resizer" @mousedown="startResize"></div>
      <ChatterDrawerHeader
        :type="type"
        :type-value="typeValue"
        :icon-name="iconName"
        :avatar="avatar"
        :is-employee-chatter="isEmployeeChatter"
        :following="following"
        @close="onClose"
        @follow-toggle="$emit('follow-toggle', $event)"
      >
        <template #follow>
          <slot name="follow" />
        </template>
      </ChatterDrawerHeader>

      <div class="chatter-drawer__filter">
        <ChatterFeedFilter :value="filter" @update:value="onFilterUpdate" @change="onFilterChange" />
      </div>

      <ChatterComposer
        class="chatter-drawer__composer"
        :disabled="composerDisabled"
        :submitting="posting"
        :clear-on-post="false"
        :value="draft"
        @input="draft = $event"
        @post="onPost"
      />

      <div class="chatter-drawer__feed">
        <div
          v-if="loading && feedItems.length === 0"
          class="chatter-drawer__loading"
          data-testid="chatter-feed-loading"
        >
          <v-progress-circular indeterminate color="primary" size="40" />
        </div>

        <div
          v-else-if="feedItems.length === 0"
          class="chatter-drawer__empty"
          data-testid="chatter-feed-empty"
        >
          <div class="chatter-drawer__empty-title">No activity yet</div>
        </div>

        <template v-else>
          <div
            v-for="group in groupedItems"
            :key="group.key"
            class="chatter-drawer__day"
          >
            <div class="chatter-drawer__day-divider">
              <span class="chatter-drawer__day-label">{{ group.label }}</span>
              <span class="chatter-drawer__day-line"></span>
            </div>
            <div v-for="item in group.items" :key="feedItemKey(item)" class="chatter-drawer__item">
              <CommentThread
                v-if="item.kind === 'comment'"
                :item="item"
                :viewer="viewer"
                :object-type="objectType"
                :object-id="objectId"
                :one-to-one="oneToOne"
                @changed="emit('changed')"
                @visible-change="onVisibleChange"
              />
              <ChatterFeedEvent
                v-else
                :actor="normalizeActor(item)"
                :relative-time="relativeTime(item)"
                :action="itemAction(item)"
                :show-badge="isHistoryItem(item) && filter === 'all'"
              />
            </div>
          </div>

          <button
            v-if="hasMore"
            type="button"
            class="chatter-drawer__load-older"
            data-testid="chatter-feed-load-older"
            @click="$emit('loadOlder')"
          >
            Load older activity
          </button>
        </template>
      </div>
    </v-navigation-drawer>
  </div>
</template>

<script setup>
import { computed, getCurrentInstance, onMounted, ref, watch } from "vue";
import ChecklistDrawer from "@components/Drawer/ChatterDrawerLegacy.vue";
import ChatterDrawerHeader from "@components/ChatterDrawerHeader/ChatterDrawerHeader.vue";
import ChatterFeedFilter from "@components/ChatterFeedFilter/ChatterFeedFilter.vue";
import ChatterFeedEvent from "@components/ChatterFeedEvent/ChatterFeedEvent.vue";
import ChatterComposer from "@components/ChatterComposer/ChatterComposer.vue";
import CommentThread from "@components/CommentThread/CommentThread.vue";
import { feedItemKey, getCommentViewer } from "@components/ChatterDrawer/composables/commentUtils";
import { useCommentActions } from "@components/ChatterDrawer/composables/useCommentActions";
import { LD_FLAG_IDEA_MDP_583 } from "@components/constants/launchDarklyFlagKeys";

const props = defineProps({
  drawer: { type: Boolean, default: false },
  iconName: { type: String, default: "org_icon_role" },
  type: { type: String, default: "" },
  typeValue: { type: String, default: "" },
  listData: { type: Object, default: null },
  // Legacy pass-through (flag off → ChecklistDrawer).
  chatterList: { type: Array, default: () => [] },
  totalCheckpointChatter: { type: Number, default: 0 },
  loading: { type: Boolean, default: false },
  isEmployeeChatter: { type: Boolean, default: false },
  avatar: { type: Object, default: () => ({}) },
  badgeStatus: { type: String, default: "" },
  // Combined feed (flag on).
  feedItems: { type: Array, default: () => [] },
  hasMore: { type: Boolean, default: false },
  following: { type: Boolean, default: false },
  composerDisabled: { type: Boolean, default: false },
  // The object whose comments this drawer shows; without them posting only emits `posted`.
  objectType: { type: String, default: "" },
  objectId: { type: [String, Number], default: "" },
  /** Show the "Not seen yet" text line instead of seen-by avatars (direct one-to-one threads). */
  oneToOne: { type: Boolean, default: false },
  /** Test/Storybook override for the idea-MDP-583 flag. When null, the LaunchDarkly flag decides. */
  enableCombinedDrawer: { type: Boolean, default: null },
});

const emit = defineEmits([
  "closeDrawer",
  "toggle-badge-status",
  "requestMoreCheckpointsChatter",
  "loadOlder",
  "posted",
  "changed",
  "filterChange",
  "follow-toggle",
]);

const instance = getCurrentInstance();
const proxy = instance?.proxy;

const combinedEnabled = ref(props.enableCombinedDrawer ?? false);
const filter = ref("all");
const drawerWidth = ref(600);
const draft = ref("");
const posting = ref(false);
const viewer = getCommentViewer();
const commentActions = useCommentActions();

const visibleByThread = new Map();
const reportedSeen = new Set();
let seenScheduled = false;

const localDrawer = computed({
  get: () => props.drawer,
  set: (value) => {
    if (!value && props.drawer) onClose();
  },
});

const filteredItems = computed(() => {
  if (filter.value === "all") return props.feedItems;
  if (filter.value === "comments") return props.feedItems.filter((i) => !isHistoryItem(i));
  if (filter.value === "history") return props.feedItems.filter((i) => isHistoryItem(i));
  return props.feedItems;
});

const groupedItems = computed(() => {
  const groups = [];
  const seen = new Map();
  for (const item of filteredItems.value) {
    const key = dayKey(item);
    if (!seen.has(key)) {
      seen.set(key, { key, label: dayLabel(item), items: [] });
      groups.push(seen.get(key));
    }
    seen.get(key).items.push(item);
  }
  return groups;
});

onMounted(async () => {
  // A non-null enableCombinedDrawer is a deterministic test/Storybook override;
  // leave the LaunchDarkly flag alone in that case.
  if (props.enableCombinedDrawer != null) return;
  await resolveFlag();
});

watch(
  () => props.enableCombinedDrawer,
  (val) => {
    if (val != null) combinedEnabled.value = val;
  }
);

async function resolveFlag() {
  try {
    if (typeof proxy?.$getFlag === "function") {
      combinedEnabled.value = Boolean(await proxy.$getFlag(LD_FLAG_IDEA_MDP_583, false));
    }
  } catch (error) {
    // Flag resolution is best-effort; fall back to the legacy drawer.
    combinedEnabled.value = false;
  }
}

function isHistoryItem(item) {
  return Boolean(item?.kind === "event" || item?.is_history || item?.IsHistory);
}

function itemDate(item) {
  return item?.comment?.created_at || item?.event_date || item?.created_on || item?.date || null;
}

function itemAction(item) {
  return item?.action || item?.message || item?.comment || "";
}

function normalizeActor(item) {
  const actor = item?.actor || item?.created_by || item?.user || {};
  return {
    firstName: actor.first_name ?? actor.firstName ?? "",
    lastName: actor.last_name ?? actor.lastName ?? "",
    picture: actor.picture ?? actor.avatar ?? "",
  };
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function parseDate(value) {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function dayKey(item) {
  const d = parseDate(itemDate(item));
  if (!d) return "unknown";
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

function dayLabel(item) {
  const d = parseDate(itemDate(item));
  if (!d) return "";
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const thatDay = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const diffDays = Math.round((today.getTime() - thatDay.getTime()) / 86400000);
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  return `${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
}

function relativeTime(item) {
  const d = parseDate(itemDate(item));
  if (!d) return "";
  const seconds = Math.floor((Date.now() - d.getTime()) / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  if (days > 0) return `${days} ${days === 1 ? "day" : "days"} ago`;
  if (hours > 0) return `${hours} ${hours === 1 ? "hour" : "hours"} ago`;
  if (minutes > 0) return `${minutes} ${minutes === 1 ? "minute" : "minutes"} ago`;
  return "just now";
}

function onClose() {
  emit("closeDrawer", props.listData);
  drawerWidth.value = 600;
}

function onLegacyClose(listData) {
  emit("closeDrawer", listData);
}

function onFilterUpdate(value) {
  filter.value = value;
}

function onFilterChange(value) {
  emit("filterChange", value);
}

async function onPost(text) {
  if (!props.objectType || props.objectId === "" || props.objectId == null) {
    emit("posted", { text, listData: props.listData });
    draft.value = "";
    return;
  }
  posting.value = true;
  const result = await commentActions.create({
    objectType: props.objectType,
    objectId: props.objectId,
    body: text,
  });
  posting.value = false;
  if (!result.ok) return;
  draft.value = "";
  emit("posted", { text, listData: props.listData, comment: result.comment });
}

// Threads report what they show as they render; one microtask later every
// comment id that is visible and not yet reported goes out in a single request.
function onVisibleChange({ threadId, ids }) {
  visibleByThread.set(threadId, ids);
  if (seenScheduled) return;
  seenScheduled = true;
  Promise.resolve().then(flushSeen);
}

async function flushSeen() {
  seenScheduled = false;
  if (!props.drawer || !combinedEnabled.value) return;
  const fresh = [...new Set([...visibleByThread.values()].flat())].filter(
    (id) => !reportedSeen.has(id)
  );
  if (!fresh.length) return;
  fresh.forEach((id) => reportedSeen.add(id));
  if (!(await commentActions.markSeen(fresh))) fresh.forEach((id) => reportedSeen.delete(id));
}

watch(
  () => props.drawer,
  (open) => {
    if (open) onVisibleChange({ threadId: null, ids: [] });
  }
);

function startResize(e) {
  const startX = e.clientX;
  const startWidth = drawerWidth.value;
  const onMove = (ev) => {
    const delta = startX - ev.clientX;
    const next = startWidth + delta;
    drawerWidth.value = next >= 600 ? Math.max(300, Math.min(next, 1000)) : 600;
  };
  const onUp = () => {
    document.body.classList.remove("no-select");
    window.removeEventListener("mousemove", onMove);
    window.removeEventListener("mouseup", onUp);
  };
  document.body.classList.add("no-select");
  window.addEventListener("mousemove", onMove);
  window.addEventListener("mouseup", onUp);
}
</script>

<style scoped>
/* Mirrors legacy Drawer/ChatterDrawer: stays above CriteriaViewEditInPlace `.checkpoint` (z-index: 50). */
.chatter-drawer {
  z-index: 100 !important;
}

.chatter-drawer__resizer {
  width: 8px;
  cursor: ew-resize;
  height: 100%;
  position: absolute;
  left: 0;
  top: 0;
  z-index: 10;
}

.chatter-drawer__filter {
  padding: 0 24px;
  margin-top: -8px;
}

.chatter-drawer__composer {
  margin-top: 8px;
}

.chatter-drawer__feed {
  flex: 1;
  overflow-y: auto;
  padding: 0 24px 24px;
}

.chatter-drawer__loading {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 180px;
}

.chatter-drawer__empty {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 160px;
}

.chatter-drawer__empty-title {
  font-family: Inter;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #667085;
}

.chatter-drawer__day {
  margin-top: 16px;
}

.chatter-drawer__day-divider {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}

.chatter-drawer__day-label {
  font-family: Inter;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  color: #475467;
  white-space: nowrap;
}

.chatter-drawer__day-line {
  flex: 1;
  height: 1px;
  background: #eaecf0;
}

.chatter-drawer__item {
  margin-bottom: 12px;
}

.chatter-drawer__load-older {
  display: block;
  width: 100%;
  margin-top: 8px;
  padding: 8px;
  border: none;
  background: transparent;
  font-family: Inter;
  font-size: 13px;
  font-weight: 500;
  line-height: 18px;
  color: #475467;
  cursor: pointer;
  text-decoration: underline;
}

.chatter-drawer__load-older:focus-visible {
  outline: 2px solid #7f56d9;
  outline-offset: 2px;
}
</style>
